/* GASSISA
=========================================================================================
* Descripción: Módulo de salidas de inventario por Almacén o Exhibidor
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 29/01/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 05/05/2026 | Refactor completo                | pcTrabajo, edición qty, cleanup
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Búsqueda multi almacén de artículos
*      4       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Bloqueo de buscador si Nueva esté activo
 *      5       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Retiro de atajos directos de eliminación
 *      6       | 30/08/2026 | Lic. José Luis Aragón Cervantes  | Alt+F12 elimina todo guardando primero la salida con confirmación
 *      7       | 23/09/2026 | LIC. José Luis Aragón C.                       | Lector estilo Conteos + captura en
 *              |            |                                  | buffer Redis con existencia fresca
 *      8       | 23/09/2026 | LIC. José Luis Aragón C.                       | Autofinado periódico silencioso
 *              |            |                                  | + finado al cerrar pestaña
 *      9       | 23/09/2026 | LIC. José Luis Aragón C.                       | Alt+F1 repetible con reintentos;
 *              |            |                                  | modal visible aunque sea EDITAR
 * ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { copiarTexto } from "../../helpers/portapapeles";

import { getDateStringV2, speakText } from "../../settings/utils.js";
import { apiSalidasGuardarTemporales } from "../../settings/ApiConfig";

import MnuSalidasAlmacenesExhibidoresActions from "./MnuSalidasAlmacenesExhibidoresActions";
import SizeSoftXLectorCodigoBarrasEntradas from "../leyout/SizeSoftXLectorCodigoBarrasEntradas";
import ModalBuscarArticulosParaVenta from "../herramientas/ModalBuscarArticulosParaVenta";
import ModalBuscarEntradasSalidas from "../herramientas/ModalBuscarEntradasSalidas";
import SizeSoftXConfirmDialog from "../leyout/SizeSoftXConfirmDialog";
import SizeSoftXAlert from "../utils/SizeSoftXAlert";
import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import SizeSoftXTableVentas from "../leyout/SizeSoftXTableVentas";
import SemaforoStatus from "../utils/SemaforoStatus";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";

import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { useSesion } from "../../context/System/SesionContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import SalidasLocalService from "../../services/SalidasLocalService";

// ─── Subcomponente: Celda de cantidad editable ──────────────────────────────────
function CantidadCell({ row, indice, onSave, onNavigate, disabled }) {
  const [valor, setValor] = useState(String(row.cantidadarticulo));
  const inputRef = useRef(null);

  useEffect(() => {
    setValor(String(row.cantidadarticulo));
  }, [row.cantidadarticulo, row.id]);

  const handleBlur = async () => {
    const nuevaCantidad = parseInt(valor, 10);
    if (valor !== "" && !isNaN(nuevaCantidad) && nuevaCantidad > 0 && nuevaCantidad !== row.cantidadarticulo) {
      const cantidadFinal = await onSave(row, nuevaCantidad);
      if (cantidadFinal) setValor(String(cantidadFinal));
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      const nuevaCantidad = parseInt(valor, 10);
      if (valor !== "" && !isNaN(nuevaCantidad) && nuevaCantidad > 0 && nuevaCantidad !== row.cantidadarticulo) {
        const cantidadFinal = await onSave(row, nuevaCantidad);
        if (cantidadFinal) setValor(String(cantidadFinal));
      }
      onNavigate(row, e.key === "ArrowUp" ? -1 : 1, indice);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(String(row.cantidadarticulo));
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={row.cantidadarticulo}
        disabled
        inputProps={{
          style: { textAlign: "center", padding: "4px 2px", width: "60px" },
        }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      data-cantidad-id={row.id}
      inputRef={inputRef}
      value={valor}
      onClick={(e) => {
  e.stopPropagation();
  e.target.select();
}}
      onFocus={(e) => {
  e.stopPropagation();
  setValor(String(row.cantidadarticulo));
  setTimeout(() => e.target.select(), 0);
}}
      onChange={(e) => setValor(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 1,
        inputMode: "numeric",
        style: { textAlign: "center", padding: "4px 2px", width: "60px" },
      }}
    />
  );
}

// ─── Componente principal ────────────────────────────────────────────────────────
export default function FrmSalidasAlmacenesExhibidores() {
  const { isLocked: isFormLocked } = useFormTabLock('salidas_almacenes', {
    title: 'Salidas Almacenes',
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || 'Salidas Almacenes'} ya está abierto en otra pestaña`);
    }
  });

  const {
    folioSalida,
    handleObtenerDatosSalidas,
    handleCargarSalidas,
    handleCargarSalidasOtraPC,
    listaSalidas,
    handleObtenerAlmacenesExhibidores,
    listaConceptosSalida,
    listaAlmacenesExhibidoresSalida,
    totalPMVB,
    totalVentaNeta,
    titleActionSave,
    handleClickNuevaGuardarSalida,
    handleClickNuevaSalida,
    totalRegistros,
    cantidadArticulos,
    // Rev 7 (23/09/2026): flujo Redis (catélogo oculto + buffer, vía RequestContext/JWE)
    handleCargarCatalogoCbarras,
    resolverArticuloCbarrasLocal,
    handleResolverCodigoEntradas,
    handleCapturarSalidaRedis,
    handleFlushSalidaRedis,
    handleCargarSalidasRedis,
    statusModalBuscadorArticulos,
    setStatusModalBuscadorArticulos,
    conceptoSalida,
    handleChangeConceptoSalida,
    handleChangeAlmacenexhibidorSalida,
    almacenExhibidorSalida,
    handleObtenerFolioSalidaDisponible,
    statusOpenDialog,
    titleOpenDialog,
    messageOpenDialog,
    handleCloseBuscarArticuloDescripcion,
    hendleEliminarPartidaEntradaSalida,
    openModal,
    setOpenModal,
    pdfUrl,
    handleCloseModarBuscarEntradaSalida,
    openModarlBuscarEntradaSalida,
    setVentanaAbierta,
    ventanaAbierta,
    statusWaiting,
    handleClickRegresarMainDashboard,
    handleEliminarDataSalida,
    handleOpenModalBuscarEntradaSalida,
    handleAplicarSalida,
    handleImprimirSalida,
    handleBuscarArticuloDescripcion,
    handleObtenerConceptosSalidas,
    handleActualizarCantidadPartidaSalida,
    statusSalida,
    openModalBuscarArticulos,
    setOpenModalBuscarArticulos,
    hendleEliminarEntradaSalida,
  } = useInventarios();

  const { parametrosCajaForm, tipoUsuario } = useSesion();

  const [partidaSeleccionada, setPartidaSeleccionada] = useState({});
  const [listaSinExistenciasSalida, setListaSinExistenciasSalida] = useState(() => SalidasLocalService.cargarSinExistencias());

  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Salidas Almacenes ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        handleSalirWrapper();
      }, 900);
    }
  }, [isFormLocked]);

  // ─── Sin scroll general: solo desplaza la tabla ──────────────────────────
  // Bloquea el overflow del contenedor del dashboard mientras este módulo esté
  // activo, para que exista una sola barra vertical (la del detalle).
  useEffect(() => {
    const el = document.querySelector('.sizesoftx-dashboard .content');
    if (!el) return;
    const prevY = el.style.overflowY;
    const prevX = el.style.overflowX;
    el.style.overflowY = 'hidden';
    el.style.overflowX = 'hidden';
    return () => {
      el.style.overflowY = prevY;
      el.style.overflowX = prevX;
    };
  }, []);

  // ─── Rev 7 (23/09/2026): carga oculta del catélogo de códigos ──────────────
  // Llena el Map local y siembra Redis en backend (por chunks, sin loader).
  useEffect(() => {
    handleCargarCatalogoCbarras().catch(() => {});
  }, []);

  // ─── PC de trabajo: siempre usar valor actual de parametrosCajaForm ───────────
  const getPcAnfitriona = () => parametrosCajaForm.caja?.trim().toUpperCase() || "";
  const [pcTrabajo, setPcTrabajo] = useState(getPcAnfitriona());

  useEffect(() => {
    setPcTrabajo(getPcAnfitriona());
  }, [parametrosCajaForm.caja]);

  const esSalidaDeOtraPC = pcTrabajo && pcTrabajo !== getPcAnfitriona() && getPcAnfitriona() !== "CAJA-DEFAULT";

  // ─── Estados locales de UI ─────────────────────────────────────────────────
  const [talla, setTalla] = useState(0);
  const [almacenError, setAlmacenError] = useState(false);
  const [openConfirmEliminarSalida, setOpenConfirmEliminarSalida] = useState(false);
  const ultimoArticuloRegistradoRef = useRef(null);
  const tableRef = useRef(null);
  const origenScannerRef = useRef(false);

  // Concepto Autocomplete
  const [conceptoSeleccionado, setConceptoSeleccionado] = useState(null);
  const [conceptoInput, setConceptoInput] = useState("");

  // Almacén Autocomplete
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState(null);
  const [almacenInput, setAlmacenInput] = useState("");

  // ─── Listas ordenadas para los Autocomplete ─────────────────────────────────
  const listaConceptosOrdenada = useMemo(
    () => (listaConceptosSalida ?? []).slice().sort((a, b) => a.concepto.localeCompare(b.concepto)),
    [listaConceptosSalida]
  );

  const listaAlmacenesExhibidoresOrdenada = useMemo(
    () =>
      (listaAlmacenesExhibidoresSalida ?? [])
        .slice()
        .sort((a, b) => a.almacenexhibidor.localeCompare(b.almacenexhibidor)),
    [listaAlmacenesExhibidoresSalida]
  );

  const listaConceptosAutoComplete = useMemo(
    () => listaConceptosOrdenada.map((item) => ({ id: item.id, concepto: item.concepto })),
    [listaConceptosOrdenada]
  );

  const listaAlmacenesExhibidoresAutoComplete = useMemo(
    () => listaAlmacenesExhibidoresOrdenada.map((item) => ({ id: item.id, nombre: item.almacenexhibidor })),
    [listaAlmacenesExhibidoresOrdenada]
  );

  // ─── Derivados ─────────────────────────────────────────────────────────────
  const tieneMovimientos = Array.isArray(listaSalidas) && listaSalidas.length > 0;
  const totalesLocalSalidas = useMemo(() => {
    const lista = Array.isArray(listaSalidas) ? listaSalidas : [];
    return {
      registros: lista.length,
      articulos: lista.reduce((s, i) => s + (parseFloat(i.cantidadarticulo) || 0), 0),
      pmvb: lista.reduce((s, i) => s + (parseFloat(i.cantidadarticulo) || 0) * (parseFloat(i.pmvb) || 0), 0),
    };
  }, [listaSalidas]);
  const statusSalidaNormalizado = (statusSalida || "").trim().toUpperCase();
  const statusPartidaCabecera = (
    (listaSalidas && listaSalidas[0])?.statusentradasalida ||
    (listaSalidas && listaSalidas[0])?.sStatusEntradaSalida ||
    ""
  ).trim().toUpperCase();
  const movimientoBloqueado =
    statusSalidaNormalizado === "TR" ||
    statusSalidaNormalizado === "CA" ||
    statusPartidaCabecera === "TR" ||
    statusPartidaCabecera === "CA";
  const modoEdicionSalida = titleActionSave === "GUARDAR";
  const funcionalidadBloqueada = movimientoBloqueado || !modoEdicionSalida;
  const buscadorArticulosBloqueado = funcionalidadBloqueada;
  const puedeAplicar = tieneMovimientos && !movimientoBloqueado && modoEdicionSalida;
  const estaPartidaBloqueada = (row) => {
    const statusPartida = (row?.statusentradasalida || row?.sStatusEntradaSalida || "").trim().toUpperCase();
    return movimientoBloqueado || statusPartida === "TR" || statusPartida === "CA";
  };

  // ─── Sync concepto desde contexto → autocomplete local ──────────────────────
  useEffect(() => {
    if (!conceptoSalida) {
      setConceptoSeleccionado(null);
      setConceptoInput("");
      return;
    }
    const found = listaConceptosAutoComplete.find(
      (item) => item.concepto === conceptoSalida
    ) || null;
    setConceptoSeleccionado(found);
    setConceptoInput(found ? found.concepto : conceptoSalida);
  }, [conceptoSalida, listaConceptosAutoComplete]);

  // ─── Sync almacén desde contexto → autocomplete local ───────────────────────
  useEffect(() => {
    if (!almacenExhibidorSalida) {
      setAlmacenSeleccionado(null);
      setAlmacenInput("");
      return;
    }
    const found = listaAlmacenesExhibidoresAutoComplete.find(
      (item) => item.nombre === almacenExhibidorSalida
    ) || null;
    setAlmacenSeleccionado(found);
    setAlmacenInput(found ? found.nombre : almacenExhibidorSalida);
    setAlmacenError(false);
  }, [almacenExhibidorSalida, listaAlmacenesExhibidoresAutoComplete]);

  // ─── Init y cleanup (unmount → restaurar PC anfitriona) ─────────────────────
  const cargandoDesdeModalRef = useRef(false);
  const nuevaSalidaEnProcesoRef = useRef(false);
  const sortedDataRef = useRef([]);
  const ultimaSalidaCargadaRef = useRef({ folio: null, pc: null });

  useEffect(() => {
    if (listaConceptosSalida.length === 0) {
      handleObtenerConceptosSalidas();
    }
    if (listaAlmacenesExhibidoresSalida.length === 0) {
      handleObtenerAlmacenesExhibidores("SALIDA");
    }

    const pending = SalidasLocalService.cargar();
    const pendingFolio = pending && pending.length > 0 ? pending[0].folioentradasalida : null;

    if (folioSalida === 0 && !pendingFolio) {
      handleObtenerFolioSalidaDisponible();
    }
    if (ventanaAbierta !== "SALIDA") {
      if (pendingFolio) {
        handleObtenerDatosSalidas(pendingFolio);
      } else {
        handleObtenerFolioSalidaDisponible();
        handleObtenerDatosSalidas(folioSalida);
      }
      setVentanaAbierta("SALIDA");
    }
    // Cleanup: al salir del módulo, restablecemos la PC anfitriona
    return () => {
      setPcTrabajo(parametrosCajaForm.caja?.trim().toUpperCase() || "");
    };
  }, []);

  useEffect(() => {
    if (folioSalida > 0 && !cargandoDesdeModalRef.current && !nuevaSalidaEnProcesoRef.current) {
      const ultima = ultimaSalidaCargadaRef.current;
      if (!(ultima.folio === folioSalida && ultima.pc === pcTrabajo)) {
        // Rev 7: lista Redis (SQL finado + buffer pendiente, recupera sesión previa).
        handleCargarSalidasRedis(folioSalida, pcTrabajo);
      }
    }
  }, [folioSalida, pcTrabajo]);

  // ─── Selección desde modal de búsqueda de salidas ───────────────────────────
  // Rev 7 (23/09/2026): antes de cambiar de folio se fina el buffer en SQL.
  const handleSelectSalidaConValidacionPC = async (salida) => {
    await guardarSalidaSiHayCambios();
    const pcSalida = salida?.pc ? String(salida.pc).trim().toUpperCase() : "";
    const pcSeleccionada = pcSalida || getPcAnfitriona();

    setPcTrabajo(pcSeleccionada);
    cargandoDesdeModalRef.current = true;

    if (pcSalida && pcSalida !== getPcAnfitriona() && getPcAnfitriona() !== "CAJA-DEFAULT") {
      await handleCargarSalidasOtraPC(salida.folio, salida.pc, salida.concepto, salida.almacenexhibidor);
    } else {
      await handleObtenerDatosSalidas(salida.folio, salida.concepto, salida.almacenexhibidor, pcSalida || getPcAnfitriona());
    }

    cargandoDesdeModalRef.current = false;
    ultimaSalidaCargadaRef.current = { folio: salida.folio, pc: pcSeleccionada };
    refocusBarcode();
  };

  // ─── Rev 7 (23/09/2026): escaneo Redis con existencia fresca ────────────────
  // Resolución O(1) en catélogo oculto -> captura en buffer Redis. La
  // existencia se valida contra SQL en CADA captura (dinémica: ventas o
  // traslados de otras méquinas) y renueva Redis en paralelo en backend.
  const handleColectorScanRedisSalida = async (codigo) => {
    if (funcionalidadBloqueada) return;
    origenScannerRef.current = true;
    const texto = String(codigo || "").trim();
    if (!texto) return;
    try {
      let articulo = resolverArticuloCbarrasLocal(texto);
      if (!articulo) articulo = await handleResolverCodigoEntradas(texto);
      if (!articulo) {
        refocusBarcode();
        return;
      }
      ultimoArticuloRegistradoRef.current = {
        clavearticulo: articulo.clavearticulo,
        medida: articulo.medida,
        almacenexhibidor: almacenExhibidorSalida,
      };
      const result = await handleCapturarSalidaRedis(
        { ...articulo, codigobarras: texto },
        1,
        esSalidaDeOtraPC ? pcTrabajo : undefined
      );
      if (result?.registrado) {
        speakText("SI PASO");
      } else if (result) {
        speakText(`Artículo ${result.descripcion || result.clavearticulo || ""} sin existencias`);
        try { setListaSinExistenciasSalida(SalidasLocalService.cargarSinExistencias()); } catch {}
      }
      refocusBarcode();
    } catch {
      refocusBarcode();
    }
  };

  // ─── Artículo seleccionado desde buscador ───────────────────────────────────
  const handleArticuloSeleccionado = async (data, almacenSeleccionadoModal) => {
    if (funcionalidadBloqueada) return;
    ultimoArticuloRegistradoRef.current = {
      clavearticulo: data?.clavearticulo,
      medida: data?.medida,
      almacenexhibidor: almacenSeleccionadoModal,
    };
    const articulo = {
      clavearticulo: data?.clavearticulo,
      descripcion: data?.descripcion,
      medida: data?.medida,
      factor: data?.factor || 1,
      pmvb: data?.pmvb || 0,
      costoventa: data?.costoventa || data?.preciodefinitivo || 0,
      codigobarras: data?.codigobarras || data?.codigoBarras || "",
    };
    const result = await handleCapturarSalidaRedis(articulo, data?.cantidad || 1, esSalidaDeOtraPC ? pcTrabajo : undefined);
    if (result) {
      setOpenModalBuscarArticulos(false);
      handleCloseBuscarArticuloDescripcion();
    }
    refocusBarcode();
  };

  // ─── Eliminar partida/salida ───────────────────────────────────────────────
  const handleEliminarData = async (title) => {
    if (title === "ELIMINAR PARTIDA") {
      await hendleEliminarPartidaEntradaSalida(partidaSeleccionada, "SALIDA");
    } else if (title === "ELIMINAR SALIDA") {
      await hendleEliminarEntradaSalida(folioSalida, "TODA LA SALIDA", esSalidaDeOtraPC ? pcTrabajo : undefined);
    }
    refocusBarcode();
  };

  // ─── Orden de registro con índice consecutivo oculto ───────────────────────
  // ─── Reserva del pie fijo al desplazar: evita que filas/inputs queden tapados ──
  // Localiza el contenedor con scroll real (ancestros) en vez de asumir selectores.
  const desplazarElementoSobrePie = (el) => {
    if (!el) return;
    let scroller = null;
    let n = el.parentElement;
    while (n) {
      if (n.scrollHeight > n.clientHeight + 1) { scroller = n; break; }
      n = n.parentElement;
    }
    const footer = document.getElementById('fixed-footer-entsal');
    const reserva = (footer?.offsetHeight || 70) + 12;
    if (!scroller) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      return;
    }
    const sr = scroller.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const limiteInferior = sr.bottom - reserva;
    if (r.bottom > limiteInferior) {
      scroller.scrollTo({ top: scroller.scrollTop + (r.bottom - limiteInferior), behavior: 'smooth' });
    } else if (r.top < sr.top + 4) {
      scroller.scrollTo({ top: scroller.scrollTop - (sr.top - r.top + 4), behavior: 'smooth' });
    }
  };

  // ─── Índice explícito de navegación: posición en el orden visible ──────────
  // Se reindexa en cada reordenamiento/carga para que las flechas avancen 1 a 1.
  const posNavRef = useRef(-1);
  const ordenVisible = () => {
    const lista = Array.isArray(listaSalidas) ? listaSalidas : [];
    return sortedDataRef.current.length > 0 ? sortedDataRef.current : lista;
  };
  const aplicarOrdenVisible = (sorted) => {
    const orden = Array.isArray(sorted) ? sorted : [];
    sortedDataRef.current = orden;
    if (orden.length === 0) { posNavRef.current = -1; return; }
    const idx = orden.findIndex((r) => r && partidaSeleccionada && r.id === partidaSeleccionada.id);
    posNavRef.current = idx >= 0 ? idx : 0;
  };

  const handleNavigateCantidad = useCallback((currentRow, direction = 1, indiceActual) => {
    const orden = ordenVisible();
    if (!orden || orden.length === 0) return;
    let pos = (typeof indiceActual === "number" && indiceActual >= 0 && indiceActual < orden.length)
      ? indiceActual
      : orden.findIndex((r) => r.id === currentRow?.id);
    if (pos < 0) pos = 0;

    let nextPos = pos + direction;
    if (nextPos < 0) nextPos = orden.length - 1;
    if (nextPos >= orden.length) nextPos = 0;
    posNavRef.current = nextPos;

    setTimeout(() => {
      // Foco por posición en el DOM (mismo orden visible), a prueba de ids duplicados
      const celdas = document.querySelectorAll('[data-cantidad-id]');
      const celda = celdas[nextPos];
      const cantidadInput = celda ? celda.querySelector('input') : null;
      if (cantidadInput) {
        cantidadInput.focus();
        cantidadInput.select();
        desplazarElementoSobrePie(cantidadInput);
      }
    }, 50);
  }, [listaSalidas]);

  const normalizarTexto = (value) => String(value ?? "").trim().toUpperCase();

  const normalizarMedida = (value) => {
    const numero = Number(value);
    return Number.isFinite(numero) ? String(numero) : normalizarTexto(value);
  };

  const textoSinExistenciasSalida = useMemo(() =>
    listaSinExistenciasSalida.map(item =>
      `${item.clavearticulo || ''} - ${item.descripcion || ''} - ${item.medida || ''} - Sol: ${item.solicitado ?? 0} - Exis: ${item.existencia ?? 0}`
    ).join('\n'),
    [listaSinExistenciasSalida]
  );

  const limpiarSinExistenciasSalida = () => {
    SalidasLocalService.limpiarSinExistencias();
    setListaSinExistenciasSalida([]);
    refocusBarcode();
  };

  const enfocarCantidadPartida = useCallback((row) => {
    setTimeout(() => {
      const cantidadInput = document.querySelector(`[data-cantidad-id="${row.id}"] input`);
      if (cantidadInput) {
        cantidadInput.focus();
        cantidadInput.select();
        desplazarElementoSobrePie(cantidadInput);
      }
    }, 80);
  }, []);

  useEffect(() => {
    const ultimoArticulo = ultimoArticuloRegistradoRef.current;
    if (!ultimoArticulo || !Array.isArray(listaSalidas) || listaSalidas.length === 0) return;

    const claveRegistrada = normalizarTexto(ultimoArticulo.clavearticulo);
    const medidaRegistrada = normalizarMedida(ultimoArticulo.medida);
    const almacenRegistrado = normalizarTexto(ultimoArticulo.almacenexhibidor);

    const partidaRegistrada = listaSalidas.find((row) => {
      const mismaClave = normalizarTexto(row.clavearticulo || row.sClaveArticulo) === claveRegistrada;
      const mismaMedida = normalizarMedida(row.medida || row.dMedida) === medidaRegistrada;
      const mismoAlmacen = !almacenRegistrado ||
        normalizarTexto(row.almacenexhibidor || row.sAlmacenExhibidor) === almacenRegistrado;

      return mismaClave && mismaMedida && mismoAlmacen;
    });

    if (!partidaRegistrada) return;

    setPartidaSeleccionada(partidaRegistrada);
    // Sincroniza el índice de navegación con el registro nuevo/sumado
    const listaActual = Array.isArray(listaSalidas) ? listaSalidas : [];
    const idxReg = listaActual.findIndex((r) => r.id === partidaRegistrada.id);
    if (idxReg >= 0) posNavRef.current = idxReg;
    setTimeout(() => {
      const bcInput = document.querySelector('#txtCodigoBarras');
      if (bcInput) bcInput.focus();
    }, 0);
    ultimoArticuloRegistradoRef.current = null;
  }, [listaSalidas, setPartidaSeleccionada, enfocarCantidadPartida]);

  useEffect(() => {
    if (origenScannerRef.current) {
      origenScannerRef.current = false;
      return;
    }
    if (titleActionSave === "EDITAR" && Array.isArray(listaSalidas) && listaSalidas.length > 0) {
      //enfocarCantidadPartida(listaSalidas[0]);
    }
  }, [titleActionSave, listaSalidas, enfocarCantidadPartida]);

  const desplazarFilaSeleccionada = () => {
    const selectedRow = tableRef.current?.querySelector('tr.row');
    if (!selectedRow) return;
    desplazarElementoSobrePie(selectedRow);
  };

  useEffect(() => {
    if (!partidaSeleccionada?.id) return;
    requestAnimationFrame(desplazarFilaSeleccionada);
    const timer = setTimeout(desplazarFilaSeleccionada, 150);
    return () => clearTimeout(timer);
  }, [partidaSeleccionada]);

  // ─── Rev 7 (23/09/2026): finado Redis -> SQL ───────────────────────────────
  // Un solo flush antes de aplicar/imprimir/nueva/salir/cambio de folio.
  // El backend toma las partidas del buffer; el frontend solo manda cabecero.
  // El aplicado mueve inventario desde las tablas SQL, nunca desde Redis.
  const guardarSalidaSiHayCambios = useCallback(async () => {
    if (movimientoBloqueado || titleActionSave !== "GUARDAR") return true;
    if (!listaSalidas || listaSalidas.length === 0) return true;
    return await handleFlushSalidaRedis(esSalidaDeOtraPC ? pcTrabajo : undefined);
  }, [movimientoBloqueado, titleActionSave, handleFlushSalidaRedis, esSalidaDeOtraPC, pcTrabajo, listaSalidas]);

  // ─── Autoguardado al salir de la ventana (desmontaje) ───────────────────────
  // Si quedan capturas sin guardar, se envían al backend para no perderlas.
  // Es no-op cuando no hay pendientes o no esté en edición. TR/CA nunca se tocan.
  const guardarAlSalirRef = useRef(null);
  guardarAlSalirRef.current = guardarSalidaSiHayCambios;
  useEffect(() => () => {
    try {
      const p = guardarAlSalirRef.current?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }, []);

  const handleGuardarSalidaWrapper = async () => {
    await guardarSalidaSiHayCambios();
    refocusBarcode();
  };

  const handleNuevaSalidaWrapper = async () => {
    if (!(await guardarSalidaSiHayCambios())) return;
    SalidasLocalService.limpiar();
    SalidasLocalService.limpiarSinExistencias();
    setListaSinExistenciasSalida([]);
    nuevaSalidaEnProcesoRef.current = true;
    try {
      await handleClickNuevaSalida();
    } finally {
      nuevaSalidaEnProcesoRef.current = false;
    }
    refocusBarcode();
  };

  const handleAplicarSalidaWrapper = async () => {
    if (!(await guardarSalidaSiHayCambios())) return;
    await handleAplicarSalida(esSalidaDeOtraPC ? pcTrabajo : undefined, true);
    setPcTrabajo(getPcAnfitriona());
    refocusBarcode();
  };

  const handleImprimirSalidaWrapper = async () => {
    if (!(await guardarSalidaSiHayCambios())) return;
    await handleImprimirSalida(esSalidaDeOtraPC ? pcTrabajo : undefined, true);
    refocusBarcode();
  };

  const handleBuscarSalidaWrapper = async () => {
    if (!(await guardarSalidaSiHayCambios())) return;
    handleOpenModalBuscarEntradaSalida();
    setTimeout(() => {
      const filtroInput = document.querySelector('input[name="Filtro"]');
      if (filtroInput) {
        filtroInput.focus();
        filtroInput.select();
      }
    }, 150);
  };

  const handleSalirWrapper = async () => {
    await guardarSalidaSiHayCambios();
    handleClickRegresarMainDashboard();
  };

  // ─── beforeunload: guardar al cerrar pestaña/navegador ─────────────────────
  useEffect(() => {
    const advertirPendientes = (e) => {
      if (movimientoBloqueado || titleActionSave !== "GUARDAR") return;
      const items = SalidasLocalService.cargar();
      if (!items || items.length === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", advertirPendientes);
    return () => window.removeEventListener("beforeunload", advertirPendientes);
  }, [movimientoBloqueado, titleActionSave]);

  // ─── Rev 8 (23/09/2026): autofinado periódico silencioso ──────────────────
  // Cada 45s fina el buffer en SQL sin loader ni recarga (el estado en
  // pantalla ya es la mezcla exacta). No interrumpe el escaneo.
  const flushSilenciosoRef = useRef(null);
  flushSilenciosoRef.current = () => {
    if (movimientoBloqueado || titleActionSave !== "GUARDAR") return Promise.resolve(true);
    if (!listaSalidas || listaSalidas.length === 0) return Promise.resolve(true);
    return handleFlushSalidaRedis(esSalidaDeOtraPC ? pcTrabajo : undefined, { silent: true }).catch(() => false);
  };
  useEffect(() => {
    const timer = setInterval(() => {
      try { flushSilenciosoRef.current?.(); } catch {}
    }, 45000);
    return () => clearInterval(timer);
  }, []);

  // ─── Rev 8: finado al cerrar la pestaña (keepalive, best-effort) ──────────
  // El buffer Redis persiste 72h de todos modos; esto solo adelanta el finado.
  useEffect(() => {
    const persistirAlOcultar = () => {
      if (movimientoBloqueado || titleActionSave !== "GUARDAR") return;
      if (!listaSalidas || listaSalidas.length === 0) return;
      try {
        const token = localStorage.getItem("sizesoftx-tenant-access-token") || "";
        if (!token || !folioSalida) return;
        fetch(apiSalidasGuardarTemporales, {
          method: "POST",
          headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
          body: JSON.stringify({
            folio: folioSalida,
            concepto: conceptoSalida || "",
            almacenexhibidor: almacenExhibidorSalida || "",
            pc: String(esSalidaDeOtraPC ? pcTrabajo : (parametrosCajaForm.caja || "")).trim().toUpperCase(),
            usuario: "",
            items: [],
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    window.addEventListener("pagehide", persistirAlOcultar);
    return () => window.removeEventListener("pagehide", persistirAlOcultar);
  }, [movimientoBloqueado, titleActionSave, listaSalidas, folioSalida, conceptoSalida, almacenExhibidorSalida, esSalidaDeOtraPC, pcTrabajo, parametrosCajaForm.caja]);

  // ─── Helpers UI ─────────────────────────────────────────────────────────────
  const refocusBarcode = useCallback(() => {
    setTimeout(() => {
      const bcInput = document.querySelector('#txtCodigoBarras');
      if (bcInput) { bcInput.focus(); bcInput.select(); }
    }, 0);
  }, []);

  const handleRowClick = (row) => {
    setPartidaSeleccionada(row);
    const orden = ordenVisible();
    const idx = orden.findIndex((item) => item.id === row?.id);
    if (idx >= 0) posNavRef.current = idx;
    refocusBarcode();
  };

  const handleEliminaPartida = async () => {
    if (!(await guardarSalidaSiHayCambios())) return;
    await hendleEliminarPartidaEntradaSalida(partidaSeleccionada, "SALIDA");
    refocusBarcode();
  };
  const handleEliminaSalida = async () => {
    await hendleEliminarEntradaSalida(folioSalida, "TODA LA SALIDA", esSalidaDeOtraPC ? pcTrabajo : undefined);
    SalidasLocalService.limpiar();
    SalidasLocalService.limpiarSinExistencias();
    setListaSinExistenciasSalida([]);
    refocusBarcode();
  };

  // Rev 9 (23/09/2026): foco al filtro con reintentos + manejador único:
  // TR/CA cierra; ya abierto trae al frente con focus+select (repetible);
  // cerrado abre solo si no esté bloqueado.
  const buscadorAbiertoRef = useRef(false);
  useEffect(() => {
    buscadorAbiertoRef.current = openModalBuscarArticulos;
  }, [openModalBuscarArticulos]);

  useEffect(() => {
    if (movimientoBloqueado && openModalBuscarArticulos) {
      setOpenModalBuscarArticulos(false);
    }
  }, [movimientoBloqueado]);

  const enfocarFiltroConReintentos = () => {
    let intentos = 0;
    const timer = setInterval(() => {
      const filtroInput = document.querySelector('input[name="Filtro"]');
      if (filtroInput) {
        filtroInput.focus();
        if (typeof filtroInput.select === "function") filtroInput.select();
        clearInterval(timer);
      } else if (++intentos > 12) {
        clearInterval(timer);
      }
    }, 60);
  };

  const manejarBuscadorArticulos = () => {
    if (movimientoBloqueado) {
      setOpenModalBuscarArticulos(false);
      return;
    }
    if (buscadorAbiertoRef.current) {
      enfocarFiltroConReintentos();
      return;
    }
    if (buscadorArticulosBloqueado) return;
    setOpenModalBuscarArticulos(true);
    handleBuscarArticuloDescripcion();
    setStatusModalBuscadorArticulos(true);
    enfocarFiltroConReintentos();
  };

  const abrirBuscadorArticulos = () => {
    manejarBuscadorArticulos();
  };

  // ─── Atajos de teclado ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.key) return;
      const key = String(event.key).trim().toUpperCase();

      if (key === "F4") {
        event.preventDefault();
        event.stopPropagation();
        handleSalirWrapper();
        return;
      }

      if (event.altKey && key === "F1") {
        event.preventDefault();
        event.stopPropagation();
        manejarBuscadorArticulos();
        return;
      }

      if (event.altKey && key === "F2") {
        event.preventDefault();
        event.stopPropagation();
        handleBuscarSalidaWrapper();
        return;
      }

      if (event.altKey && key === "A") {
        event.preventDefault();
        event.stopPropagation();
        if (puedeAplicar) handleAplicarSalidaWrapper();
        return;
      }

      if (event.altKey && key === "I") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneMovimientos) handleImprimirSalidaWrapper();
        return;
      }

      if (event.altKey && key === "F12") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneMovimientos) setOpenConfirmEliminarSalida(true);
        return;
      }

      const target = event.target;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isInput) return;

      const orden = ordenVisible();
      if (!orden || orden.length === 0) return;
      let pos = posNavRef.current;
      if (pos < 0 || pos >= orden.length) {
        const idx = orden.findIndex(item => partidaSeleccionada && item.id === partidaSeleccionada.id);
        pos = idx >= 0 ? idx : 0;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        let newPos = pos;
        if (event.key === 'ArrowDown') {
          newPos = pos < orden.length - 1 ? pos + 1 : pos;
        } else {
          newPos = pos > 0 ? pos - 1 : 0;
        }
        posNavRef.current = newPos;
        if (orden[newPos]) {
          setPartidaSeleccionada(orden[newPos]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [tieneMovimientos, movimientoBloqueado, buscadorArticulosBloqueado, puedeAplicar, pcTrabajo, parametrosCajaForm.caja, listaSalidas, partidaSeleccionada, handleBuscarSalidaWrapper]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Fragment>
        <SizeSoftXLayoutForm
          titlePage="Módulo de salidas Almacenes/Exhibidores"
          buttonActions={
            <MnuSalidasAlmacenesExhibidoresActions
              onOpenBuscador={abrirBuscadorArticulos}
              onImprimir={handleImprimirSalidaWrapper}
              onAplicar={handleAplicarSalidaWrapper}
              onEliminarPartida={handleEliminaPartida}
              onEliminarSalida={handleEliminaSalida}
              buscadorArticulosBloqueado={buscadorArticulosBloqueado}
              onRefocusBarcode={refocusBarcode}
              accionesBloqueadas={funcionalidadBloqueada}
              onBuscarSalida={handleBuscarSalidaWrapper}
              onSalir={handleSalirWrapper}
              onGuardarEdicion={handleGuardarSalidaWrapper}
              onNueva={handleNuevaSalidaWrapper}
            />
          }
        >
          <div className="conteiner-form-general">
            <div className="conteiner-form-left">
              <div className="body-form">
                <TextField autoComplete="off"
                  name="foliosalida"
                  label="Folio Salida:"
                  value={folioSalida ? folioSalida : 0}
                  sx={{ width: "500px", height: "6.3vh" }}
                  disabled
                />
                <TextField autoComplete="off"
                  name="fechasalida"
                  label="Fecha:"
                  value={
                    listaSalidas.length > 0
                      ? listaSalidas[0].fecharegistro != null
                        ? getDateStringV2(listaSalidas[0].fecharegistro)
                        : getDateStringV2(Date())
                      : getDateStringV2(Date())
                  }
                  sx={{ width: "500px", height: "6.3vh" }}
                  disabled
                />
                <SemaforoStatus
                  status={listaSalidas.length > 0 ? listaSalidas[0].statusentradasalida : ""}
                />
                {esSalidaDeOtraPC && (
                  <TextField autoComplete="off"
                    name="pcOrigen"
                    label="PC Origen:"
                    value={pcTrabajo}
                    sx={{ width: "200px", height: "6.3vh" }}
                    disabled
                    color="warning"
                    focused
                  />
                )}
              </div>

              <div className="body-form">
                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-concepto-salida"
                  options={listaConceptosAutoComplete}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.concepto
                  }
                  value={conceptoSeleccionado}
                  inputValue={conceptoInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setConceptoSeleccionado(null);
                      setConceptoInput(newValue);
                      handleChangeConceptoSalida(event, newValue);
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      setConceptoSeleccionado(newValue);
                      setConceptoInput(newValue.concepto);
                      handleChangeConceptoSalida(event, newValue.concepto);
                      return;
                    }
                    setConceptoSeleccionado(null);
                    setConceptoInput("");
                    handleChangeConceptoSalida(event, "");
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setConceptoSeleccionado(null);
                      setConceptoInput(newInputValue);
                      handleChangeConceptoSalida(event, newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Concepto salida:" required />
                  )}
                  sx={{ width: "500px", height: "6.3vh" }}
                />

                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-almacen-exhibidor"
                  options={listaAlmacenesExhibidoresAutoComplete}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.nombre
                  }
                  value={almacenSeleccionado}
                  inputValue={almacenInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setAlmacenSeleccionado(null);
                      setAlmacenInput(newValue);
                      const norm = newValue.trim().toUpperCase();
                      const valid = listaAlmacenesExhibidoresAutoComplete.some(
                        (item) => item.nombre.trim().toUpperCase() === norm
                      );
                      setAlmacenError(!valid && newValue.length > 0);
                      handleChangeAlmacenexhibidorSalida(event, newValue);
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      setAlmacenSeleccionado(newValue);
                      setAlmacenInput(newValue.nombre);
                      setAlmacenError(false);
                      handleChangeAlmacenexhibidorSalida(event, newValue.nombre);
                      return;
                    }
                    setAlmacenSeleccionado(null);
                    setAlmacenInput("");
                    setAlmacenError(false);
                    handleChangeAlmacenexhibidorSalida(event, "");
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setAlmacenInput(newInputValue);
                      const norm = newInputValue.trim().toUpperCase();
                      const valid = listaAlmacenesExhibidoresAutoComplete.some(
                        (item) => item.nombre.trim().toUpperCase() === norm
                      );
                      setAlmacenError(!valid && newInputValue.length > 0);
                      handleChangeAlmacenexhibidorSalida(event, newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Almacén o Exhibidor:"
                      required
                      error={almacenError}
                      helperText={almacenError ? "Seleccione un almacén vélido de la lista" : ""}
                    />
                  )}
                  sx={{ width: "300px", height: "6.3vh" }}
                />
              </div>

              <div className="body-Detalles-entradas-salidas" style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                {/* Espacio inferior para que el pie fijo no tape las últimas filas al hacer scroll */}
                <div ref={tableRef} style={{ flex: '1 1 auto', minWidth: 0, paddingBottom: '1cm' }}>
                  <SizeSoftXTableVentas
                  columns={[
                    { title: "Clave artículo", field: "clavearticulo", width: 90, render: (row) => (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {row.clavearticulo}
                        <IconButton size="small" title="Copiar código de barras" aria-label="Copiar código de barras" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); copiarTexto(row.codigobarras || row.codigoBarras || row.clavearticulo); }}>
                          <ContentCopy fontSize="inherit" sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    ) },
                    { title: "Almacen/Exhibidor", field: "almacenexhibidor", width: 130 },
                    { title: "#", field: "_captureIndex", width: 45 },
                    { title: "Descripción", field: "descripcion", width: 270 },
                    { title: "Medida", field: "medida", width: 70 },
                    {
                      title: "Cantidad",
                      field: "cantidadarticulo",
                      render: (row, indice) => (
                        <CantidadCell
                          key={`qty-${row.id}-${row.cantidadarticulo}`}
                          row={row}
                          indice={indice}
                          disabled={funcionalidadBloqueada || estaPartidaBloqueada(row)}
                          onSave={handleActualizarCantidadPartidaSalida}
                          onNavigate={handleNavigateCantidad}
                        />
                      ),
                      width: 70,
                    },
                    { title: "PMVB", field: "pmvb", width: 70 },
                    { title: "P. Definitivo", field: "costoventa", width: 70 },
                  ]}
                    data={listaSalidas ?? []}
                  id="id"
                  row={partidaSeleccionada}
                  setRow={handleRowClick}
                  fontSize="small"
                  disablePagination
                  onSortChange={aplicarOrdenVisible}
                />
                </div>
                <Box sx={{
                  flex: '0 0 280px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minWidth: '240px',
                }}>
                  <TextField autoComplete="off"
                    label="SIN EXISTENCIAS"
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={10}
                    value={textoSinExistenciasSalida}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'inherit',
                        fontFamily: 'monospace',
                        fontSize: '1.1rem',
                        lineHeight: 1.35,
                      },
                      '& .MuiInputBase-input': {
                        color: '#b71c1c',
                        fontWeight: 500,
                      },
                      '& .MuiInputLabel-root': {
                        color: '#b71c1c',
                        fontWeight: 600,
                      },
                    }}
                  />
                  <button type="button" onClick={limpiarSinExistenciasSalida} disabled={listaSinExistenciasSalida.length === 0}>
                    Limpiar lista
                  </button>
                </Box>
              </div>

            </div>
          </div>
        </SizeSoftXLayoutForm>

        <Box
          id="fixed-footer-entsal"
          sx={{
            position: 'fixed',
            bottom: '10px',
            left: '20px',
            right: '20px',
            borderRadius: '12px',
            zIndex: 1100,
            backgroundColor: '#fff',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: '8px',
            borderTop: '1px solid #ddd',
          }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <SizeSoftXLectorCodigoBarrasEntradas
              onColectorScan={handleColectorScanRedisSalida}
              disabled={funcionalidadBloqueada}
            />
          </Box>
          <TextField autoComplete="off"
            label="Total registros"
            value={totalesLocalSalidas.registros}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          <TextField autoComplete="off"
            label="Cantidad artículos"
            value={totalesLocalSalidas.articulos}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          <TextField autoComplete="off"
            label="Total PMVB"
            value={totalesLocalSalidas.pmvb}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          {(titleOpenDialog === "Módulo de salidas Almacenes/Exhibidores" || tipoUsuario === "ADMINISTRADOR") && (
            <TextField autoComplete="off"
              label="Venta neta"
              value={totalVentaNeta}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ flex: '0 0 130px' }}
            />
          )}
        </Box>

        <ModalBuscarArticulosParaVenta
          openModal={openModalBuscarArticulos && !movimientoBloqueado}
          handleCloseModal={() => {
            setOpenModalBuscarArticulos(false);
            handleCloseBuscarArticuloDescripcion();
            refocusBarcode();
          }}
          dominio={almacenExhibidorSalida || parametrosCajaForm.dominio}
          consultarTodosAlmacenes={true}
          onTallaSelect={(e) => setTalla(e)}
          articuloSelect={handleArticuloSeleccionado}
        />

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box sx={{ width: "80%", height: "80vh", margin: "auto", background: "white", padding: 2 }}>
            {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" />}
          </Box>
        </Modal>

        <SizeSoftXConfirmDialog
          open={statusOpenDialog}
          title={titleOpenDialog}
          message={messageOpenDialog}
          handleConfirmDialog={() => handleEliminarData(titleOpenDialog)}
        />

        <SizeSoftXConfirmDialog
          open={openConfirmEliminarSalida}
          title="Eliminar todo"
          message="Se guardaré la salida actual y se limpiaré la captura. ¿Desea continuar?"
          handleConfirmDialog={(confirm) => {
            setOpenConfirmEliminarSalida(false);
            if (confirm) handleNuevaSalidaWrapper();
          }}
        />

        <ModalBuscarEntradasSalidas
          tipo={"SALIDA"}
          openModal={openModarlBuscarEntradaSalida}
          handleCloseModal={() => { handleCloseModarBuscarEntradaSalida(); refocusBarcode(); }}
          onSelectSalida={handleSelectSalidaConValidacionPC}
        />

        <SizeSoftXAlert />
        <SizeSoftXLoadingProcess
          open={statusWaiting}
          message="Procesando información, por favor espere..."
        />
      </Fragment>
    </>
  );
}
